import React, { useEffect } from 'react';
import { Form, FormInstance, Input, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import FormSection from 'components/ui/form-section';
import FormLayoutGroup from 'components/ui/form-layout-group';
import { ArrowDownIcon } from 'assets/icons/arrow-down';
import { CountrySelection } from 'components/ui/country-selection';
import { EQueryKeys } from 'constants/query-keys';
import { useQuery } from 'react-query';
import { scheduleOfValuesApi } from 'services/apis';
import { useWatch } from 'antd/es/form/Form';

interface IncidentalSectionProps {
  submissionId: string;
  form: FormInstance;
  disabled?: boolean;
}

const IncidentalSection: React.FC<IncidentalSectionProps> = ({
  submissionId,
  form,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const { data: countryListData } = useQuery(
    [EQueryKeys.SOV_COUNTRIES, submissionId],
    () =>
      scheduleOfValuesApi.getSovCountryList({
        submissionId: submissionId,
      }),
    {
      refetchOnWindowFocus: false,
    }
  );
  const incidental = useWatch<boolean>('incidental', form);

  useEffect(() => {
    if (incidental === false) {
      form.setFieldsValue({
        incidentalCountry: [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidental]);

  return (
    <FormSection title={t('contractDetail.incidental')} collapsible>
      <FormLayoutGroup mdColumns={2}>
        <Form.Item
          label={t('contractDetail.stockDeclaration')}
          name="stockDeclaration"
        >
          <Select
            size="large"
            suffixIcon={<ArrowDownIcon />}
            options={[
              { value: true, label: t('general.yes') },
              { value: false, label: t('general.no') },
            ]}
            disabled={disabled}
          />
        </Form.Item>
        <Form.Item
          label={t('contractDetail.primaryRiskLocation')}
          name="primaryRiskLocation"
        >
          <Input disabled size="large" />
        </Form.Item>
      </FormLayoutGroup>

      <FormLayoutGroup mdColumns={2}>
        <Form.Item label={t('contractDetail.incidental')} name="incidental">
          <Select
            size="large"
            suffixIcon={<ArrowDownIcon />}
            options={[
              { value: true, label: t('general.yes') },
              { value: false, label: t('general.no') },
            ]}
            disabled={disabled}
          />
        </Form.Item>

        <CountrySelection
          mode="multiple"
          label={t('contractDetail.incidentalCountry')}
          countryList={countryListData}
          disabled={!incidental || disabled}
          name="incidentalCountry"
          includePlaceHolder={false}
        />
      </FormLayoutGroup>

      <FormLayoutGroup mdColumns={1}>
        <Form.Item
          label={t('contractDetail.underwritingComments')}
          name="underwritingComments"
        >
          <Input.TextArea
            rows={4}
            size="large"
            placeholder={disabled ? '' : t('contractDetail.addYourReasonHere')}
            disabled={disabled}
          />
        </Form.Item>
      </FormLayoutGroup>
    </FormSection>
  );
};

export default IncidentalSection;
